'use client';

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import dynamic from 'next/dynamic';
import gsap from 'gsap';
import { useTranslations } from 'next-intl';
import { CarouselButton } from 'components/shared/CarouselButton';
import {
  CustomImage,
  getSpeedsizeImageUrl,
} from 'components/shared/custom-image/CustomImage';
import type {
  LiquidCardHover,
  LiquidCardLayout,
  LiquidCardMotion,
} from './LiquidCardCanvas';
import styles from './LiquidCardCarousel.module.css';

const LiquidCardCanvas = dynamic(() => import('./LiquidCardCanvas'), {
  ssr: false,
});

export type LiquidCard = {
  key: string;
  src: string;
  alt: string;
  /** Photo box on the card as (x, y, w, h) shares of the card, as the design
      places it; covers the card when left out. */
  crop?: [number, number, number, number];
  /** Card UI drawn over the photo, laid out at designSize; it travels with
      the card and scales with it. */
  overlay?: ReactNode;
  /** Copy under the card; it travels with the card. */
  caption?: ReactNode;
};

export type LiquidCardCarouselProps = {
  /** 7 to 12 (repeats are fine), so a wide screen never shows one card twice. */
  cards: LiquidCard[];
  /** Accessible name of the carousel region. */
  label: string;
  sizes: string;
  /** Card size the overlays are laid out at, in px; also sets the ratio. */
  designSize?: [number, number];
  /** Seconds a chevron takes to move the row one card. */
  slide?: number;
  ease?: string;
  /** Continuous drift speed in px per 60fps frame, as Embla AutoScroll's
      `speed`; false turns it off. Eases to a stop on hover, keyboard focus,
      off screen and in a background tab. */
  autoScroll?: number | false;
  /** Prev / next chevrons over the left and right edges. */
  arrows?: boolean;
  /** Neighbouring cards stay joined by a liquid neck as they cross the outer
      band. Off keeps the edge warp but lets each card move on its own. */
  link?: boolean;
  /** Sets --card-w, --gap (px) and --card-radius. */
  className?: string;
};

const MOTION_OK_MQ = '(prefers-reduced-motion: no-preference)';
const HOVER_MQ = '(hover: hover)';
/* Photo zoom on the hovered card, as HoverPhotoCards. */
const HOVER_ZOOM = 1.04;
/* Must match LIQUID_CARD.calm: the middle share that slides without distortion. */
const CALM = 0.78;
/* Velocity fed to the shader while drifting, which turns on the edge necks.
   A chevron glide peaks at it too, so a click stretches no more than the drift. */
const DRIFT_VEL = 0.15;

const wrap = (n: number, count: number) => ((n % count) + count) % count;
const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};
/* Width the canvas asks the image CDN for; photos run past the card. */
const TEXTURE_WIDTH = 1600;

/* Offset of card i from the centre, in cards, wrapped to either side. */
const offsetOf = (i: number, pos: number, count: number) =>
  wrap(i - pos + count / 2, count) - count / 2;

/* The ease's speed over its progress, normalised to peak at 1. */
function velocityCurve(ease: string) {
  const fn = gsap.parseEase(ease);
  const h = 0.002;
  const speed = (p: number) => {
    const a = Math.max(0, p - h);
    const b = Math.min(1, p + h);
    return (fn(b) - fn(a)) / (b - a);
  };
  let peak = 0;
  for (let i = 0; i <= 200; i += 1) peak = Math.max(peak, speed(i / 200));
  return (p: number) => speed(p) / peak;
}

/* Full-width looping card row that drifts continuously and glides one card
   at a time from its chevrons. Where WebGL is available LiquidCardCanvas
   draws the cards: the middle stays clean, and as a card crosses the outer
   band it bends through a glass bevel and stays joined to its neighbour by a
   liquid neck for a moment. Cards never change size. Elsewhere the DOM cards
   blur at the ends and run through an SVG goo filter while gliding; reduced
   motion gets a plain cross-fade. Captions stay in the DOM, so text is crisp
   and translated. */
export const LiquidCardCarousel = ({
  cards,
  label,
  sizes,
  designSize = [385, 513],
  slide = 1.1,
  ease = 'sine.inOut',
  autoScroll = 1,
  arrows = true,
  link = true,
  className,
}: LiquidCardCarouselProps) => {
  const t = useTranslations('common');
  const gooId = `liquid-goo-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const stageRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const slotRefs = useRef<Array<HTMLDivElement | null>>([]);
  const overlayRefs = useRef<Array<HTMLDivElement | null>>([]);
  const captionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const motionRef = useRef<LiquidCardMotion>({ pos: 0, vel: 0 });
  const layoutRef = useRef<LiquidCardLayout>({
    width: 1,
    height: 1,
    cardW: 1,
    cardH: 1,
    gap: 0,
    radius: 0,
  });
  const renderRef = useRef<() => void>(() => undefined);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const hoverRef = useRef<LiquidCardHover>({
    idx: -1,
    amt: 0,
    outIdx: -1,
    outAmt: 0,
  });
  const busyRef = useRef(false);
  const queueRef = useRef(0);
  const glReadyRef = useRef(false);
  const [glEnabled, setGlEnabled] = useState(false);
  const [glReady, setGlReady] = useState(false);
  const [current, setCurrent] = useState(0);
  const currentRef = useRef(0);
  const [gliding, setGliding] = useState(false);
  const [coasting, setCoasting] = useState(false);
  const driftRef = useRef({ factor: 0 });
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [onScreen, setOnScreen] = useState(false);
  const [tabVisible, setTabVisible] = useState(true);

  const count = cards.length;
  const [designW, designH] = designSize;
  const ratio = designW / designH;
  const glCards = useMemo(
    () =>
      cards.map((card) => ({
        src: getSpeedsizeImageUrl(card.src, TEXTURE_WIDTH),
        crop: card.crop,
      })),
    [cards],
  );

  /* DOM cards, used until (or instead of) the canvas. Mirrors the shader:
     calm in the middle, blurred towards the edges. */
  const layoutDom = useCallback(() => {
    if (glReadyRef.current) return;
    const { pos, vel } = motionRef.current;
    const { width, cardW, gap } = layoutRef.current;
    const half = width / 2;
    const speed = Math.abs(vel);
    slotRefs.current.forEach((slot, i) => {
      if (!slot) return;
      const x = offsetOf(i, pos, count) * (cardW + gap);
      const calm = Math.min(Math.max(CALM, cardW / 2 / half), 0.97);
      const v = speed * smoothstep(calm, 1, Math.abs(x) / half);
      slot.style.transform = `translate3d(${x}px, 0, 0)`;
      slot.style.filter = v > 0.02 ? `blur(${(v * 1.5).toFixed(2)}px)` : '';
    });
    /* Goo only while a chevron glides; the slow drift stays sharp. */
    if (link && rowRef.current) {
      rowRef.current.style.filter =
        busyRef.current && speed > 0 ? `url(#${gooId})` : '';
    }
  }, [count, gooId, link]);

  /* Overlays and captions ride with their cards, so the text never leaves. */
  const placeLayers = useCallback(() => {
    const { pos } = motionRef.current;
    const { cardW, gap } = layoutRef.current;
    [overlayRefs.current, captionRefs.current].forEach((layer) =>
      layer.forEach((node, i) => {
        if (!node) return;
        const x = offsetOf(i, pos, count) * (cardW + gap);
        node.style.transform = `translate3d(${x}px, 0, 0)`;
      }),
    );
  }, [count]);

  /* Zooms the photo of the card under the pointer; the one it left eases
     back. The canvas reads hoverRef, the DOM cards get a transform. */
  const zoomDom = useCallback(() => {
    const h = hoverRef.current;
    slotRefs.current.forEach((slot, i) => {
      const photo = slot?.firstElementChild as HTMLElement | null;
      if (!photo) return;
      let z = 0;
      if (i === h.idx) z = h.amt;
      else if (i === h.outIdx) z = h.outAmt;
      photo.style.transform = z ? `scale(${1 + (HOVER_ZOOM - 1) * z})` : '';
    });
  }, []);

  const updateHover = useCallback(() => {
    const pointer = pointerRef.current;
    const { width, cardW, cardH, gap } = layoutRef.current;
    let idx = -1;
    if (pointer) {
      const pitch = cardW + gap;
      const slot = Math.round(
        (pointer.x - width / 2) / pitch + motionRef.current.pos,
      );
      const centre = width / 2 + (slot - motionRef.current.pos) * pitch;
      const pad = (layoutRef.current.height - cardH) / 2;
      if (
        Math.abs(pointer.x - centre) <= cardW / 2 &&
        pointer.y >= pad &&
        pointer.y <= pad + cardH
      ) {
        idx = wrap(slot, count);
      }
    }
    const h = hoverRef.current;
    if (idx === h.idx) return;
    gsap.killTweensOf(h);
    h.outIdx = h.idx;
    h.outAmt = h.amt;
    h.idx = idx;
    h.amt = 0;
    gsap.to(h, {
      amt: idx < 0 ? 0 : 1,
      outAmt: 0,
      duration: 0.5,
      ease: 'power2.out',
      onUpdate: () => {
        zoomDom();
        renderRef.current();
      },
    });
  }, [count, zoomDom]);

  const tick = useCallback(() => {
    layoutDom();
    placeLayers();
    updateHover();
    renderRef.current();
    const index = wrap(Math.round(motionRef.current.pos), count);
    if (index !== currentRef.current) {
      currentRef.current = index;
      setCurrent(index);
    }
  }, [count, layoutDom, placeLayers, updateHover]);

  useEffect(() => {
    const stage = stageRef.current;
    const frame = frameRef.current;
    if (!stage || !frame) return undefined;
    const measure = () => {
      const s = stage.getBoundingClientRect();
      const f = frame.getBoundingClientRect();
      const gap = parseFloat(getComputedStyle(stage).getPropertyValue('--gap'));
      layoutRef.current = {
        width: s.width,
        height: s.height,
        cardW: f.width,
        cardH: f.height,
        gap: gap || 0,
        radius: parseFloat(getComputedStyle(frame).borderTopLeftRadius) || 0,
      };
      stage.style.setProperty('--overlay-scale', String(f.width / designW));
      tick();
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [designW, tick]);

  /* Probes WebGL, and so loads three and the textures, only once the row is
     a screen away. */
  useEffect(() => {
    const node = stageRef.current;
    if (!node || !window.matchMedia(MOTION_OK_MQ).matches) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        io.disconnect();
        const probe = document.createElement('canvas');
        const gl = probe.getContext('webgl2') ?? probe.getContext('webgl');
        gl?.getExtension('WEBGL_lose_context')?.loseContext();
        setGlEnabled(!!gl);
      },
      { rootMargin: '100% 0px' },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const onGlReady = useCallback(() => {
    glReadyRef.current = true;
    setGlReady(true);
    if (rowRef.current) rowRef.current.style.filter = '';
  }, []);

  /* A chevron glides to the neighbouring card, then the drift picks up again. */
  const go = useCallback(
    (step: 1 | -1) => {
      if (count < 2) return;
      if (busyRef.current) {
        queueRef.current = Math.max(-2, Math.min(2, queueRef.current + step));
        return;
      }
      busyRef.current = true;
      setGliding(true);

      const m = motionRef.current;
      const to =
        step > 0 ? Math.floor(m.pos + 1e-6) + 1 : Math.ceil(m.pos - 1e-6) - 1;
      const reduced = !window.matchMedia(MOTION_OK_MQ).matches;

      const finish = () => {
        busyRef.current = false;
        const queued = queueRef.current;
        if (queued !== 0) {
          const next = queued > 0 ? 1 : -1;
          queueRef.current -= next;
          go(next);
          return;
        }
        setGliding(false);
      };

      if (reduced) {
        const fading = [
          rowRef.current,
          ...overlayRefs.current,
          ...captionRefs.current,
        ].filter(Boolean);
        const tl = gsap.timeline({ onComplete: finish });
        tl.to(fading, { autoAlpha: 0, duration: 0.2 });
        tl.call(() => {
          m.pos = to;
          tick();
        });
        tl.to(fading, { autoAlpha: 1, duration: 0.2 });
        return;
      }

      const velocity = velocityCurve(ease);
      gsap.to(m, {
        pos: to,
        duration: slide,
        ease,
        onUpdate(this: gsap.core.Tween) {
          m.vel = step * DRIFT_VEL * velocity(this.progress());
          tick();
        },
        onComplete() {
          m.vel = 0;
          tick();
          finish();
        },
      });
    },
    [count, ease, slide, tick],
  );

  useEffect(() => {
    const node = stageRef.current;
    if (!node) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setOnScreen(!!entry?.isIntersecting),
      { threshold: 0.25 },
    );
    io.observe(node);
    const onVisibility = () => setTabVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  /* Continuous drift, as the codebase's Embla AutoScroll carousels run
     (EndlessAutoScrollCarousel, speed in px per 60fps frame). The pace eases
     in and out instead of starting or stopping dead. */
  useEffect(() => {
    if (autoScroll === false || count < 2) return undefined;
    if (!window.matchMedia(MOTION_OK_MQ).matches) return undefined;
    const m = motionRef.current;
    const drift = driftRef.current;
    /* Own rAF loop, stepped by the frame timestamp, so each frame moves by
       exactly the time that frame took. */
    let raf = 0;
    let last = 0;
    const onFrame = (now: number) => {
      raf = requestAnimationFrame(onFrame);
      const deltaMs = last ? Math.min(now - last, 50) : 0;
      last = now;
      if (busyRef.current || drift.factor <= 0.001) return;
      const { cardW, gap } = layoutRef.current;
      const pitch = cardW + gap;
      if (pitch <= 1) return;
      m.pos += (autoScroll * 0.06 * deltaMs * drift.factor) / pitch;
      m.vel = DRIFT_VEL * drift.factor;
      tick();
    };
    raf = requestAnimationFrame(onFrame);
    return () => cancelAnimationFrame(raf);
  }, [autoScroll, count, tick]);

  const drifting =
    autoScroll !== false && !hovered && !focused && onScreen && tabVisible;
  useEffect(() => {
    const m = motionRef.current;
    if (drifting) setCoasting(true);
    gsap.to(driftRef.current, {
      factor: drifting ? 1 : 0,
      duration: 0.6,
      ease: 'sine.inOut',
      overwrite: true,
      onComplete: () => {
        if (drifting) return;
        setCoasting(false);
        if (busyRef.current) return;
        m.vel = 0;
        tick();
      },
    });
  }, [drifting, tick]);

  return (
    <div
      className={[styles.root, className].filter(Boolean).join(' ')}
      style={{ '--card-ratio': String(ratio) } as CSSProperties}
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={(event) => {
        if (event.target.matches(':focus-visible')) setFocused(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          setFocused(false);
        }
      }}
    >
      <div
        ref={stageRef}
        className={styles.stage}
        data-gl={glReady ? '' : undefined}
        onPointerMove={(event) => {
          if (event.pointerType !== 'mouse') return;
          if (!window.matchMedia(`${HOVER_MQ} and ${MOTION_OK_MQ}`).matches) {
            return;
          }
          const box = event.currentTarget.getBoundingClientRect();
          pointerRef.current = {
            x: event.clientX - box.left,
            y: event.clientY - box.top,
          };
          updateHover();
        }}
        onPointerLeave={() => {
          pointerRef.current = null;
          updateHover();
        }}
      >
        <div ref={rowRef} className={styles.row} aria-hidden="true">
          {cards.map((card, i) => (
            <div
              key={card.key}
              ref={(node) => {
                slotRefs.current[i] = node;
              }}
              className={styles.slot}
            >
              <div
                className={styles.photo}
                style={
                  card.crop
                    ? {
                        left: `${card.crop[0] * 100}%`,
                        top: `${card.crop[1] * 100}%`,
                        width: `${card.crop[2] * 100}%`,
                        height: `${card.crop[3] * 100}%`,
                      }
                    : { inset: 0 }
                }
              >
                <CustomImage src={card.src} alt="" fill sizes={sizes} />
              </div>
            </div>
          ))}
        </div>

        {glEnabled ? (
          <LiquidCardCanvas
            cards={glCards}
            ratio={ratio}
            motionRef={motionRef}
            layoutRef={layoutRef}
            hoverRef={hoverRef}
            renderRef={renderRef}
            live={gliding || coasting}
            link={link}
            onReady={onGlReady}
          />
        ) : null}

        <div className={styles.overlays} aria-hidden="true">
          {cards.map((card, i) =>
            card.overlay ? (
              <div
                key={card.key}
                ref={(node) => {
                  overlayRefs.current[i] = node;
                }}
                className={styles.overlay}
              >
                <div
                  className={styles.overlayArt}
                  style={{ width: designW, height: designH }}
                >
                  {card.overlay}
                </div>
              </div>
            ) : null,
          )}
        </div>

        {/* The centre card's box: sets the stage height and the card size. */}
        <div ref={frameRef} className={styles.frame} aria-hidden="true" />

        {arrows && count > 1 ? (
          <>
            <CarouselButton
              direction="prev"
              variant="glass"
              className={`${styles.arrow} ${styles.prev}`}
              aria-label={t(
                'productCarousel.carouselButton.ariaLabel.previousSlide',
              )}
              onClick={() => go(-1)}
            />
            <CarouselButton
              direction="next"
              variant="glass"
              className={`${styles.arrow} ${styles.next}`}
              aria-label={t(
                'productCarousel.carouselButton.ariaLabel.nextSlide',
              )}
              onClick={() => go(1)}
            />
          </>
        ) : null}
      </div>

      <div className={styles.captions}>
        {cards.map((card, i) => (
          <div
            key={card.key}
            ref={(node) => {
              captionRefs.current[i] = node;
            }}
            className={styles.caption}
            role="group"
            aria-roledescription="slide"
            aria-label={card.alt}
            aria-current={i === current ? 'true' : undefined}
          >
            {card.caption}
          </div>
        ))}
      </div>

      <svg className={styles.defs} aria-hidden="true" focusable="false">
        <filter id={gooId}>
          <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="blur" />
          <feColorMatrix
            in="blur"
            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 18 -7"
            result="goo"
          />
          <feComposite in="SourceGraphic" in2="goo" operator="atop" />
        </filter>
      </svg>
    </div>
  );
};
